/**
 * FloodClaimsSection — PRP-FLOOD-1 work item 3 (FL2): one switch for NFIP flood claims.
 * Off (the default) = Cat 3 tear-out lines are the bagged WTR items (IICRC S500,
 * decision 0052). On = the no-bagging items, for carriers or adjusters that do not pay
 * for bagging. Saves on toggle and reverts the switch if the save fails. A setting
 * change never rewrites an existing estimate; check_scope only suggests.
 * Rendered from HeadingsSection with the page's own `Section` frame.
 */
import { useEffect, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';

import { getScopeSettings, updateScopeSettings } from '../../lib/scopeSettingsApi';
import { Switch } from './ui/switch';

interface FrameProps {
  title: string;
  sub?: string;
  children: ReactNode;
  footer?: ReactNode;
}

export const FLOOD_LABEL = 'Use no-bagging tear-out items on NFIP flood claims';
export const FLOOD_HINT =
  'IICRC S500 calls for bagging Category 3 debris. Turn this on if your carrier or adjuster does not pay for bagging.';

type Load = 'loading' | 'ready' | 'unavailable' | 'failed';

export default function FloodClaimsSection({ frame: Frame }: { frame: ComponentType<FrameProps> }) {
  const [on, setOn] = useState(false);
  const [load, setLoad] = useState<Load>('loading');
  const [busy, setBusy] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'ok' | 'bad'; msg: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    getScopeSettings()
      .then((s) => {
        if (cancelled) return;
        setOn(Boolean(s?.nfip_no_bagging));
        setLoad('ready');
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const status = (e as { status?: number } | null)?.status;
        setLoad(status === 404 ? 'unavailable' : 'failed');
      });
    return () => {
      cancelled = true;
    };
  }, []);

  async function onToggle(next: boolean) {
    const prev = on;
    setOn(next);
    setBusy(true);
    setFlash(null);
    try {
      const out = await updateScopeSettings({ nfip_no_bagging: next });
      setOn(Boolean(out?.nfip_no_bagging ?? next));
      setFlash({ kind: 'ok', msg: 'Saved.' });
    } catch {
      setOn(prev);
      setFlash({ kind: 'bad', msg: 'Could not save. The setting is unchanged.' });
    } finally {
      setBusy(false);
    }
  }

  const disabled = load !== 'ready' || busy;

  return (
    <Frame title="Flood claims">
      <div className="space-y-2" data-testid="flood-claims">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <label id="flood-nfip-no-bagging-label" htmlFor="flood-nfip-no-bagging"
              className="block text-[13px] text-[var(--tx-hi)]">
              {FLOOD_LABEL}
            </label>
            <p id="flood-nfip-no-bagging-hint" className="m-0 mt-[2px] text-[11.5px] text-[var(--tx-faint)]">
              {FLOOD_HINT}
            </p>
          </div>
          <Switch id="flood-nfip-no-bagging" data-testid="flood-nfip-no-bagging" className="mt-[2px]"
            aria-labelledby="flood-nfip-no-bagging-label" aria-describedby="flood-nfip-no-bagging-hint"
            checked={on} disabled={disabled} onCheckedChange={onToggle} />
        </div>
        {load === 'unavailable' && (
          <p className="m-0 text-[12px] text-[var(--tx-lo)]" data-testid="flood-unavailable">
            Not available yet.
          </p>
        )}
        {load === 'failed' && (
          <p className="m-0 text-[12.5px] text-[var(--bad)]" data-testid="flood-flash">
            Could not load this setting. Try again later.
          </p>
        )}
        {flash && (
          <p className={`m-0 text-[12.5px] ${flash.kind === 'ok' ? 'text-[var(--ok)]' : 'text-[var(--bad)]'}`}
            role="status" data-testid="flood-flash">
            {flash.msg}
          </p>
        )}
      </div>
    </Frame>
  );
}
